import { useState, useCallback, useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import { EditorContent } from '@tiptap/react';
import {
  sanitizeRichTextContent,
  useTheme,
  type LessonCompleteBlock,
  type LessonCompleteTitleLevel,
  type CourseBlock,
  type SuccessAnimationType,
} from '@course-authoring/shared-types';
import {
  useIsMobileViewport,
  LESSON_COMPLETE_PRESET_IMAGES,
} from '@course-authoring/ui-blocks';
import { ImageInsertModal, type ImageInsertData } from './ImageInsertModal';
import { type ImageAreaState } from './blockEditorStyles';
import { createRichTextEditorExtensions } from './richTextEditorExtensions';
import { RichTextToolbar } from './richTextToolbar';
import { useRichTextFieldEditor } from './useRichTextFieldEditor';
import {
  getInteractionRichTextFieldPresetConfig,
  createTokenRichTextToolbarStyles,
} from './interactionEditorUtils';

type SuccessAnimationSelection = 'none' | SuccessAnimationType;

const SUCCESS_ANIMATION_OPTIONS: Array<{
  value: SuccessAnimationSelection;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'confetti', label: 'Confetti' },
  { value: 'pixie-dust', label: 'Pixie Dust' },
  { value: 'gold-coins', label: 'Gold Coins' },
];

const TITLE_LEVEL_OPTIONS: LessonCompleteTitleLevel[] = ['h1', 'h2', 'h3', 'h4'];

const MOBILE_BLOCK_WIDTH = '90%';
const DESKTOP_IMAGE_WIDTH = '70%';
const DESKTOP_TEXT_WIDTH = '60%';
const DESKTOP_LEFT_INSET = '10%';

function isPresetImage(url: string | undefined): boolean {
  if (!url) return false;
  return LESSON_COMPLETE_PRESET_IMAGES.some((preset) => preset.src === url);
}

interface LessonCompleteEditorProps {
  block: LessonCompleteBlock;
  onChange: (updates: Partial<CourseBlock>) => void;
  translationMode?: boolean;
}

export function LessonCompleteEditor({ block, onChange, translationMode = false }: LessonCompleteEditorProps) {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageAreaState, setImageAreaState] = useState<ImageAreaState>({
    isFocused: false,
    isHovered: false,
  });
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);

  const titleLevel = block.titleLevel ?? 'h2';
  const useLeftInset = !isMobile;
  const imageWrapperWidth = isMobile ? MOBILE_BLOCK_WIDTH : DESKTOP_IMAGE_WIDTH;
  const textWrapperWidth = isMobile ? MOBILE_BLOCK_WIDTH : DESKTOP_TEXT_WIDTH;

  const descriptionContent = useMemo(
    () => sanitizeRichTextContent(block.description as JSONContent),
    [block.description]
  );

  const fullPresetConfig = useMemo(() => getInteractionRichTextFieldPresetConfig('full'), []);
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);

  const richTextExtensions = useMemo(
    () =>
      createRichTextEditorExtensions('Enter description...', fullPresetConfig.extensionOptions),
    [fullPresetConfig.extensionOptions]
  );

  const descriptionEditor = useRichTextFieldEditor({
    extensions: richTextExtensions,
    content: descriptionContent,
    onContentChange: (content) => onChange({ description: content } as Partial<CourseBlock>),
    onFocus: () => setIsDescriptionFocused(true),
    onBlur: () => setIsDescriptionFocused(false),
    containerRef,
    editorClassName: 'tiptap lesson-complete-description-editor',
    editorInlineStyle: 'outline: none; min-height: 60px;',
    autoFocus: false,
    shouldRerenderOnTransaction: true,
  });

  const handleImageInsert = useCallback(
    (data: ImageInsertData) => {
      if (!data.src) {
        onChange({
          imageUrl: undefined,
          imageAlt: undefined,
          imageLibraryItemId: undefined,
        } as Partial<CourseBlock>);
        setIsImageModalOpen(false);
        return;
      }

      const trimmedAlt = data.alt.trim();
      onChange({
        imageUrl: data.src,
        imageAlt: trimmedAlt.length > 0 ? trimmedAlt : 'Image',
        imageLibraryItemId: data.libraryItemId,
      } as Partial<CourseBlock>);
      setIsImageModalOpen(false);
    },
    [onChange]
  );

  const handlePresetSelect = useCallback(
    (presetSrc: string, presetLabel: string) => {
      onChange({
        imageUrl: presetSrc,
        imageAlt: presetLabel,
        imageLibraryItemId: undefined,
      } as Partial<CourseBlock>);
    },
    [onChange]
  );

  const handleRemoveImage = useCallback(() => {
    onChange({
      imageUrl: undefined,
      imageAlt: undefined,
      imageLibraryItemId: undefined,
    } as Partial<CourseBlock>);
  }, [onChange]);

  const headingTypography = {
    h1: tokens.typography.h1,
    h2: tokens.typography.h2,
    h3: tokens.typography.h3,
    h4: tokens.typography.h3,
  } as const;
  const headingStyle = headingTypography[titleLevel];

  const hasCustomImage = Boolean(block.imageUrl) && !isPresetImage(block.imageUrl);

  const styles: Record<string, CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      width: '100%',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: useLeftInset ? 'flex-start' : 'center',
      justifyContent: 'flex-start',
      gap: tokens.spacing.lg,
      width: '100%',
    },
    imageSection: {
      width: imageWrapperWidth,
      maxWidth: imageWrapperWidth,
      marginLeft: useLeftInset ? DESKTOP_LEFT_INSET : 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    imageSourceLabel: {
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: tokens.text.muted,
    },
    presetRow: {
      display: 'flex',
      gap: tokens.spacing.sm,
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    presetThumb: {
      width: '64px',
      height: '64px',
      borderRadius: tokens.radius.md,
      border: `2px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      cursor: 'pointer',
      padding: '4px',
      objectFit: 'contain',
      transition: 'border-color 150ms',
      boxSizing: 'border-box',
    },
    presetThumbActive: {
      borderColor: tokens.ui.selectionBorder,
      boxShadow: `0 0 0 1px ${tokens.ui.selectionBorder}`,
    },
    customUploadButton: {
      width: '64px',
      height: '64px',
      borderRadius: tokens.radius.md,
      border: `2px dashed ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2px',
      color: tokens.text.muted,
      fontSize: '10px',
      fontFamily: tokens.typography.fontFamily,
      fontWeight: 500,
      transition: 'border-color 150ms, background-color 150ms',
      boxSizing: 'border-box',
    },
    customUploadButtonActive: {
      borderColor: tokens.ui.selectionBorder,
      borderStyle: 'solid',
      boxShadow: `0 0 0 1px ${tokens.ui.selectionBorder}`,
    },
    removeButton: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      fontSize: '12px',
      fontWeight: 500,
      color: tokens.text.muted,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      alignSelf: 'flex-start',
    },
    imagePreview: {
      width: '100%',
      borderRadius: tokens.radius.md,
      overflow: 'hidden',
      backgroundColor: 'transparent',
    },
    imagePreviewInner: {
      position: 'relative',
      width: '100%',
      ...(hasCustomImage
        ? { display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }
        : { display: 'flex', justifyContent: 'center' }),
    },
    imagePreviewSurface: {
      ...(hasCustomImage
        ? {
            position: 'relative',
            display: 'inline-block',
            maxWidth: '100%',
          }
        : {
            position: 'static',
          }),
    },
    imagePreviewImg: {
      ...(hasCustomImage
        ? {
            display: 'block',
            maxWidth: '100%',
            maxHeight: `calc(${imageWrapperWidth} * 0.5)`,
            borderRadius: tokens.radius.md,
          }
        : { maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' as const }),
      cursor: hasCustomImage ? 'pointer' : 'default',
    },
    imageOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: imageAreaState.isHovered || imageAreaState.isFocused ? 1 : 0,
      transition: 'opacity 150ms',
      cursor: 'pointer',
    },
    imageOverlayText: {
      color: tokens.text.onInverse,
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      fontWeight: 600,
    },
    textWrapper: {
      width: textWrapperWidth,
      maxWidth: textWrapperWidth,
      marginLeft: useLeftInset ? DESKTOP_LEFT_INSET : 0,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    titleStyleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.xs,
    },
    titleStyleLabel: {
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: tokens.text.muted,
    },
    titleStyleButton: {
      minWidth: '40px',
      padding: '4px 8px',
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    titleStyleButtonActive: {
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onInverse,
      borderColor: tokens.ui.selectionBorder,
    },
    titleInput: {
      width: '100%',
      border: 'none',
      borderBottom: `1px solid ${tokens.borders.default}`,
      outline: 'none',
      backgroundColor: 'transparent',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: headingStyle.size,
      fontWeight: headingStyle.weight,
      lineHeight: headingStyle.lineHeight,
      padding: `${tokens.spacing.sm} 0`,
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: `${tokens.spacing.xs} 0`,
    },
    descriptionEditorContainer: {
      borderBottom: `1px solid ${tokens.borders.default}`,
      paddingBottom: tokens.spacing.sm,
      minHeight: '80px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      lineHeight: tokens.typography.body.lineHeight,
    },
    animationField: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginTop: tokens.spacing.xs,
    },
    animationLabel: {
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: tokens.text.muted,
    },
    animationSelect: {
      padding: '4px 8px',
      fontSize: '14px',
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box',
    },
  };

  const descriptionResetStyles = `
    .lesson-complete-description-editor p {
      margin: 0;
    }
    .lesson-complete-description-editor p:not(:last-child) {
      margin-bottom: 0.5em;
    }
  `;

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={styles.content}>
        <div style={styles.imageSection}>
          {!translationMode ? <><span style={styles.imageSourceLabel}>Image:</span>
          <div style={styles.presetRow}>
            {LESSON_COMPLETE_PRESET_IMAGES.map((preset) => (
              <img
                key={preset.id}
                src={preset.src}
                alt={preset.label}
                title={preset.label}
                style={{
                  ...styles.presetThumb,
                  ...(block.imageUrl === preset.src ? styles.presetThumbActive : {}),
                }}
                onClick={() => handlePresetSelect(preset.src, preset.label)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePresetSelect(preset.src, preset.label);
                  }
                }}
              />
            ))}
            <button
              type="button"
              style={{
                ...styles.customUploadButton,
                ...(hasCustomImage ? styles.customUploadButtonActive : {}),
              }}
              onClick={() => setIsImageModalOpen(true)}
              title="Upload custom image"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              Custom
            </button>
          </div></> : null}

          {block.imageUrl ? (
            <>
              <div
                style={styles.imagePreview}
                onMouseEnter={() => setImageAreaState((prev) => ({ ...prev, isHovered: true }))}
                onMouseLeave={() => setImageAreaState((prev) => ({ ...prev, isHovered: false }))}
              >
                <div style={styles.imagePreviewInner}>
                  <div style={styles.imagePreviewSurface}>
                    <img
                      src={block.imageUrl}
                      alt={block.imageAlt ?? ''}
                      style={styles.imagePreviewImg}
                      onClick={translationMode ? undefined : () => hasCustomImage ? setIsImageModalOpen(true) : undefined}
                    />
                    {!translationMode && hasCustomImage ? (
                      <div
                        style={styles.imageOverlay}
                        onClick={() => setIsImageModalOpen(true)}
                      >
                        <span style={styles.imageOverlayText}>Click to change image</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              {!translationMode ? <button
                type="button"
                style={styles.removeButton}
                onClick={handleRemoveImage}
              >
                Remove image
              </button> : null}
            </>
          ) : null}
        </div>

        <div style={styles.textWrapper}>
          {!translationMode ? <div style={styles.titleStyleRow}>
            <span style={styles.titleStyleLabel}>Title style:</span>
            {TITLE_LEVEL_OPTIONS.map((level) => (
              <button
                key={level}
                type="button"
                style={{
                  ...styles.titleStyleButton,
                  ...(titleLevel === level ? styles.titleStyleButtonActive : {}),
                }}
                aria-pressed={titleLevel === level}
                onClick={() => onChange({ titleLevel: level } as Partial<CourseBlock>)}
              >
                {level.toUpperCase()}
              </button>
            ))}
          </div> : null}

          <input
            type="text"
            value={block.title}
            onChange={(e) => onChange({ title: e.target.value } as Partial<CourseBlock>)}
            placeholder="Enter title..."
            style={styles.titleInput}
            aria-label="Lesson complete title"
          />

          <style>{descriptionResetStyles}</style>
          {descriptionEditor ? (
            <div style={{
              ...(isDescriptionFocused ? {} : { opacity: 0.5, pointerEvents: 'none' as const }),
            }}>
              <RichTextToolbar
                editor={descriptionEditor}
                items={fullPresetConfig.toolbarItems}
                toolbarStyle={toolbarStyles.toolbar}
                buttonStyle={toolbarStyles.button}
                activeButtonStyle={toolbarStyles.activeButton}
                dividerStyle={toolbarStyles.divider}
              />
            </div>
          ) : null}
          <div style={styles.descriptionEditorContainer}>
            {descriptionEditor ? <EditorContent editor={descriptionEditor} /> : null}
          </div>

          {!translationMode ? <div style={styles.animationField}>
            <span style={styles.animationLabel}>Animation:</span>
            <select
              aria-label="Success animation"
              value={block.successAnimationType ?? 'none'}
              onChange={(e) => {
                const value = e.target.value as SuccessAnimationSelection;
                onChange({
                  successAnimationType: value === 'none' ? undefined : value,
                } as Partial<CourseBlock>);
              }}
              style={styles.animationSelect}
            >
              {SUCCESS_ANIMATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div> : null}
        </div>
      </div>

      {!translationMode ? <ImageInsertModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onInsert={handleImageInsert}
        targetAspectRatio="2:1"
        initialImage={
          block.imageUrl && hasCustomImage
            ? { src: block.imageUrl, alt: block.imageAlt }
            : undefined
        }
      /> : null}
    </div>
  );
}

LessonCompleteEditor.displayName = 'LessonCompleteEditor';
